import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';
import { Copy, Check, X, ExternalLink } from 'lucide-react';

// Lightweight singleton dialog for desktop share flows. We don't use the
// shadcn Dialog because every other site surface that needs to trigger
// "share this video" already calls `shareVideo()` — wiring a per-call
// context provider would mean editing 7+ call sites. Instead we listen on
// a window-level custom event so any caller can pop the dialog without
// importing the component.
//
// On mobile (`navigator.share` available), `shareVideo` short-circuits
// to the native sheet and never fires this event, so the dialog is
// effectively desktop-only.
export const SHARE_DIALOG_EVENT = 'audivue:open-share-dialog';

const ENC = encodeURIComponent;

export const ShareDialog = () => {
  const [open, setOpen] = useState(false);
  const [video, setVideo] = useState(null);
  const [url, setUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (ev) => {
      const { video: v, url: u } = (ev && ev.detail) || {};
      if (!u) return;
      setVideo(v || null);
      setUrl(u);
      setCopied(false);
      setOpen(true);
    };
    window.addEventListener(SHARE_DIALOG_EVENT, handler);
    return () => window.removeEventListener(SHARE_DIALOG_EVENT, handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      // Pre-select the URL so a desktop user can ⌘C even without clicking
      // the Copy button.
      try {
        inputRef.current.focus();
        inputRef.current.select();
      } catch (_e) { /* no-op */ }
    }
  }, [open]);

  if (!open) return null;

  const close = () => {
    setOpen(false);
    setCopied(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied to clipboard');
      // Auto-dismiss the "copied" check after a moment, but keep the
      // dialog open so the user can also click one of the social buttons
      // if they want.
      setTimeout(() => setCopied(false), 1800);
    } catch (_e) {
      // Fallback for older browsers / non-https contexts: select the input
      // and exec the deprecated command. Better than failing silently.
      try {
        inputRef.current?.select();
        document.execCommand('copy');
        setCopied(true);
        toast.success('Link copied to clipboard');
        setTimeout(() => setCopied(false), 1800);
      } catch (_e2) {
        toast.error('Could not copy. Long-press the link to copy manually.');
      }
    }
  };

  const title = video?.title || 'AI Music Video';
  const artist = video?.artist || 'an artist';
  const tweet = `${title} by ${artist} on AUDIVUE`;

  const socials = [
    {
      name: 'X',
      testid: 'share-dialog-x',
      href: `https://twitter.com/intent/tweet?text=${ENC(tweet)}&url=${ENC(url)}`,
      // X / Twitter brand mark via lucide isn't quite right; use a text glyph.
      glyph: 'X',
    },
    {
      name: 'Facebook',
      testid: 'share-dialog-facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${ENC(url)}`,
      glyph: 'f',
    },
    {
      name: 'WhatsApp',
      testid: 'share-dialog-whatsapp',
      href: `https://wa.me/?text=${ENC(`${tweet} ${url}`)}`,
      glyph: 'W',
    },
  ];

  return (
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 p-4"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-dialog-title"
      data-testid="share-dialog"
    >
      <div
        className="w-full max-w-md bg-zinc-950 border border-zinc-800 rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <h3 id="share-dialog-title" className="text-white font-semibold text-base">Share this {video?.content_type === 'film' ? 'film' : 'video'}</h3>
          <button
            onClick={close}
            className="text-zinc-500 hover:text-white"
            aria-label="Close share dialog"
            data-testid="share-dialog-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          {video?.title && (
            <div className="text-sm text-zinc-300 leading-snug">
              <span className="text-zinc-500">Sharing </span>
              <span className="text-white font-medium">{video.title}</span>
              {video.artist && (
                <>
                  <span className="text-zinc-500"> by </span>
                  <span className="text-white">{video.artist}</span>
                </>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              readOnly
              value={url}
              onClick={(e) => e.currentTarget.select()}
              className="flex-1 bg-zinc-900 border border-zinc-800 text-white rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-fuchsia-500/50 select-all"
              data-testid="share-dialog-url"
            />
            <button
              onClick={copyLink}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                copied
                  ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                  : 'bg-fuchsia-500 hover:bg-fuchsia-600 text-white'
              }`}
              data-testid="share-dialog-copy"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>

          <div className="pt-1">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
              Or share to
            </div>
            <div className="grid grid-cols-3 gap-2">
              {socials.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200 text-sm transition-colors"
                  data-testid={s.testid}
                >
                  <span className="font-bold w-5 text-center">{s.glyph}</span>
                  <span className="hidden sm:inline">{s.name}</span>
                  <ExternalLink className="w-3 h-3 text-zinc-500" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareDialog;
